<script lang="ts">
import { formatBlogDate } from '$lib/utils/dates.js';
import { formatReadingTime } from '$lib/utils/readingTime.js';

interface Props {
    title?: string;
    date?: string;
    readingTimeMinutes?: number | null;
    category?: {
        value?: {
            name: string;
        };
    };
    author?: {
        name: string;
        role?: string;
        linkedIn?: string;
    };
}

let { title = '', date = '', readingTimeMinutes = null, category = {}, author }: Props = $props();

let dateUpdated = $derived(new Date(date));
let readingTimeLabel = $derived(formatReadingTime(readingTimeMinutes));
</script>

<div class="blog-post-header">
    <div class="blog-post-header__wrapper">
        {#if category?.value?.name}
            <div class="category">
                {category.value.name}
            </div>
        {/if}
        <h1 class="blog-post-title"><span>{title}</span></h1>
        <div class="date">
            <time datetime={dateUpdated.toISOString()}>
                {formatBlogDate(date)}
            </time>
            {#if readingTimeLabel}
                <span class="date__separator" aria-hidden="true">|</span>
                <span>{readingTimeLabel}</span>
            {/if}
        </div>
        {#if author?.name && author.name.trim()}
            <div class="author">
                {#if author.linkedIn}
                    <a
                        href={author.linkedIn}
                        target="_blank"
                        rel="noopener noreferrer"
                        aria-label="{author.name} on LinkedIn (opens in new tab)"
                    >
                        {author.name}
                    </a>
                {:else}
                    {author.name}
                {/if}
                {#if author.role}
                    <span class="author-role">, {author.role}</span>
                {/if}
            </div>
        {/if}
    </div>
</div>

<style lang="scss">
    @use '/src/scss/global-constants/typography';
    @use 'sass-mq/mq' as *;

    .blog-post-header {
        display: grid;
        place-items: center;
        &__wrapper {
            border-bottom: 1px solid var(--colour-noghrei-silver);
            margin: 0 var(--layout-pagegrid-margin) 2.5rem;
            max-width: 76rem;
            padding-bottom: 4.4rem;
            @include mq($from: tablet) {
                margin-bottom: 5rem;
            }
        }
    }

    .blog-post-title {
        @include typography.display-article;
        padding-bottom: 1.6rem;
        text-align: center;
        text-wrap: balance;
        @include mq($from: tablet) {
            padding-bottom: 2.8rem;
        }

        span {
            display: block;
            margin: 0 auto;
            max-width: 40ch;
        }
    }

    .category,
    .date,
    .author {
        @include typography.body-lg;
        text-align: center;

        &:not(:last-child) {
            padding-bottom: 1.6rem;
            @include mq($from: tablet) {
                padding-bottom: 2.8rem;
            }
        }
    }

    .date {
        color: var(--colour-squant);
        align-items: center;
        display: flex;
        flex-wrap: wrap;
        gap: 0.8rem;
        justify-content: center;
    }

    .date__separator {
        color: inherit;
    }

    .author {
        color: var(--colour-squant);

        a {
            color: inherit;
            text-decoration: none;

            &:hover,
            &:focus {
                text-decoration: underline;
            }
        }
    }
</style>
